import { ReactNode, useCallback, useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { OperatorMap, SelectOption } from "@via/utils/interfaces";
import { createMongoFilter, classNames } from "@via/utils/helpers";
import FilterRow from "@via/components/advanced-filter/filter-row";
import {
  FieldType,
  FilterItem,
} from "@via/components/advanced-filter/helpers/types";
import { PlusIcon } from "@heroicons/react/24/outline";

export interface AdvancedFilterProps {
  fields: FieldType[];
  filters: (FilterItem & { operator?: string })[];
  onFiltersChange: (
    filters: (FilterItem & { operator?: string })[],
    logic?: "AND" | "OR"
  ) => void;
  operatorMap: OperatorMap;
  className?: string;
  maxFilters?: number;
  disabled?: boolean;
  debugMode?: boolean;
  filterLogic?: "AND" | "OR";
  rightActions?: ReactNode;
}

interface FilterFormData {
  filters: (FilterItem & { operator?: string })[];
}

export default function AdvancedFilter({
  fields,
  filters,
  onFiltersChange,
  operatorMap,
  className = "",
  maxFilters = 10,
  disabled = false,
  debugMode = false,
  filterLogic = "OR",
  rightActions,
}: AdvancedFilterProps) {
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: {
      filters: filters.length > 0 ? filters : [],
    },
  });

  const {
    fields: formFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "filters",
  });

  const watchedFilters = watch("filters");
  const [currentLogic, setCurrentLogic] = useState<"AND" | "OR">(filterLogic);

  // Sync currentLogic with filterLogic prop
  useEffect(() => {
    setCurrentLogic(filterLogic);
  }, [filterLogic]);

  // Convert fields to SelectOption format
  const fieldOptions: SelectOption[] = fields.map(field => ({
    label: field.label,
    value: field.name,
  }));

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (
      newFilters: (FilterItem & { operator?: string })[],
      logic?: "AND" | "OR"
    ) => {
      onFiltersChange(newFilters, logic || currentLogic);
    },
    [onFiltersChange, currentLogic]
  );

  // Handle adding a new filter
  const handleAddFilter = useCallback(() => {
    if (formFields.length < maxFilters) {
      append({ id: "", value: "", operator: "" }); // Default to "equals" operator
    }
  }, [append, formFields.length, maxFilters]);

  // Handle removing a filter
  const handleRemoveFilter = useCallback(
    (index: number) => {
      // Get current filters before removal
      const currentFilters = watch("filters");

      // Remove the filter from the form
      remove(index);

      // Calculate what the filters will be after removal
      const updatedFilters =
        currentFilters?.filter((_, i) => i !== index) || [];

      // Check if there are any valid filters left after removal
      const validFilters = updatedFilters.filter(
        filter =>
          filter.id &&
          filter.value !== undefined &&
          filter.value !== "" &&
          filter.operator
      );

      // If no valid filters remain, automatically clear the table
      if (validFilters.length === 0) {
        handleFiltersChange([], currentLogic);
      }
      // If there are still valid filters, don't auto-apply - user must click Apply button
    },
    [remove, watch, handleFiltersChange, currentLogic]
  );

  // Handle applying filter manually
  const handleApplyFilter = useCallback(() => {
    const currentFilters = watch("filters");

    if (currentFilters) {
      // Filter out incomplete filters (missing id, value, or operator)
      const validFilters = currentFilters.filter(
        filter =>
          filter.id &&
          filter.value !== undefined &&
          filter.value !== "" &&
          filter.operator
      );

      handleFiltersChange(validFilters, currentLogic);
    }
  }, [watch, handleFiltersChange, currentLogic]);

  // Check if there are any valid filters to apply
  const hasValidFilters = useCallback(() => {
    const currentFilters = watch("filters");
    if (!currentFilters) return false;

    return currentFilters.some(
      filter =>
        filter.id &&
        filter.value !== undefined &&
        filter.value !== "" &&
        filter.operator
    );
  }, [watch]);

  // Handle logic change
  const handleLogicChange = useCallback(
    (newLogic: "AND" | "OR") => {
      if (currentLogic !== newLogic) {
        setCurrentLogic(newLogic);
      }
    },
    [currentLogic]
  );

  // Generate MongoDB filter using the helper function
  const mongoFilter = createMongoFilter(watchedFilters, operatorMap);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">Advanced Filters</h3>
        <div className="flex items-center space-x-3">
          {/* AND/OR Toggle Button Group */}
          {formFields.length > 0 && (
            <span className="isolate inline-flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => handleLogicChange("AND")}
                className={classNames(
                  "relative inline-flex h-9 items-center rounded-l-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                  currentLogic === "AND"
                    ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                    : "bg-white"
                )}>
                AND
              </button>
              <button
                type="button"
                onClick={() => handleLogicChange("OR")}
                className={classNames(
                  "relative -ml-px inline-flex h-9 items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                  currentLogic === "OR"
                    ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                    : "bg-white"
                )}>
                OR
              </button>
            </span>
          )}

          {/* Add Filter Button */}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleAddFilter}
              disabled={disabled || formFields.length >= maxFilters}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
              <PlusIcon className="h-5 w-5 mr-1" />
              Add Filter
            </button>
            {rightActions}
          </div>
        </div>
      </div>

      <div className="space-y-3 max-h-120 overflow-auto">
        {formFields.map((field, index) => (
          <FilterRow
            key={field.id}
            control={control}
            index={index}
            fieldOptions={fieldOptions}
            fields={fields}
            onRemove={() => handleRemoveFilter(index)}
          />
        ))}
      </div>

      {/* Apply Button */}
      {formFields.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleApplyFilter}
            disabled={!hasValidFilters()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            Apply Filters
          </button>
        </div>
      )}

      {/* Debug: Show generated MongoDB filter */}
      {debugMode && mongoFilter && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
          <h4 className="text-xs font-medium text-gray-700 mb-2">
            Generated Filter (Debug):
          </h4>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(mongoFilter, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
